import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, BookOpen, Users, Award } from 'lucide-react';
import './Hero.css';

const Hero = () => {
  const heroRef = useRef(null);
  
  // Removed scroll-triggered transforms to prevent sections from disappearing
  
  const isInView = useInView(heroRef, { once: true, margin: '-100px' });

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const headerHeight = 80;
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const elementTop = rect.top + scrollTop;
      const targetPosition = elementTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section ref={heroRef} className="hero">
      <div className="hero-content">
        <div className="container">
          <motion.div
            className="hero-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="hero-text">
              <motion.div variants={itemVariants} className="hero-badge">
                <div className="uk-flag">
                  <div className="flag-container">
                    <div className="flag-blue"></div>
                    <div className="flag-cross-white"></div>
                    <div className="flag-cross-red"></div>
                    <div className="flag-diagonal-white"></div>
                    <div className="flag-diagonal-red"></div>
                  </div>
                </div>
                <span className="badge-text">UK's Finest</span>
                <div className="badge-shine"></div>
              </motion.div>
              
              <motion.h1 variants={itemVariants} className="hero-title">
                One-on-One
                <span className="gradient-text"> <span className="no-break">Online Tuition</span></span>
              </motion.h1>
              
              <motion.p variants={itemVariants} className="hero-description">
                At our core, every student matters. We've been helping learners unlock their full potential through 
                personalised, one-on-one tutoring. By matching each student with a dedicated tutor, we provide 
                tailored learning that simplifies understanding, boosts confidence, and builds strong academic 
                foundations — shaping tomorrow's leaders.
              </motion.p>
              
              <motion.div variants={itemVariants} className="hero-subjects">
                <p className="subjects-text">We cover SATs, 11+ exam, GCSE, A-level & IB</p>
              </motion.div>
              
              <motion.div variants={itemVariants} className="hero-actions">
                <button className="btn-primary" onClick={scrollToContact}>
                  Book Free Consultation
                  <ArrowRight className="btn-icon" />
                </button>
              </motion.div>
              
              <motion.div variants={itemVariants} className="hero-stats">
                <div className="stat">
                  <div className="stat-number">1000+</div>
                  <div className="stat-label">Students Helped</div>
                </div>
                <div className="stat">
                  <div className="stat-number">£6.90</div>
                  <div className="stat-label">Starting Price</div>
                </div>
                <div className="stat">
                  <div className="stat-number">98%</div>
                  <div className="stat-label">Success Rate</div>
                </div>
              </motion.div>
            </div>
            
            <div className="hero-visual">
              <motion.div 
                className="visual-container"
                variants={floatingVariants}
                animate="animate"
              >
                <div className="floating-card card-1">
                  <BookOpen className="card-icon" />
                  <span>Personalized Learning</span>
                </div>
                
                <div className="floating-card card-2">
                  <Users className="card-icon" />
                  <span>Expert Tutors</span>
                </div>
                
                <div className="floating-card card-3">
                  <Award className="card-icon" />
                  <span>Proven Results</span>
                </div>
                
                <div className="central-logo">
                  <img src="/logo.png" alt="SkillBourn Logo" className="logo-image" />
                </div>
                
                <div className="orbit-ring ring-1"></div>
                <div className="orbit-ring ring-2"></div>
                <div className="orbit-ring ring-3"></div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
    </section>
  );
};

export default Hero;
